import React, { useEffect, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  accountLogoImgHtml,
  collectRemovableLayoutCardNodes,
  listRemovableLayoutCards,
  parseBrandColors,
  rewriteTotalDueRow,
  stripEditorChrome,
} from './layoutCards';

const PreviewKicker = ({ label, busy, t }) => (
  <div className="mcc-preview-kicker">
    <span>{label}</span>
    {busy ? (
      <span className="mcc-preview-busy">
        <CircularProgress size={14} />
        {t('MessageControl.BusyPreview', { defaultValue: 'Updating preview…' })}
      </span>
    ) : null}
  </div>
);
const LINK_RE = /(https?:\/\/[^\s]+|ilocal\.link\/[^\s]+)/gi;
const TOKEN_RE = /\{\{\s*([^}]+)\s*\}\}/g;

const escapePreviewText = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const mergePreviewText = (text, tokens = {}) =>
  String(text || '').replace(TOKEN_RE, (full, raw) => {
    const key = String(raw || '').trim();
    if (tokens[key] != null && tokens[key] !== '') {
      return String(tokens[key]);
    }
    return full;
  });

const tokenPreviewValue = (name, tokens = {}) => {
  const key = String(name || '').trim();
  if (tokens[key] != null && tokens[key] !== '') {
    return tokens[key];
  }
  const wanted = key.toLowerCase().replace(/[\s_-]+/g, '');
  const found = Object.keys(tokens).find(
    (item) => item.toLowerCase().replace(/[\s_-]+/g, '') === wanted
  );
  if (found && tokens[found] != null && tokens[found] !== '') {
    return tokens[found];
  }
  return null;
};

const noteSlotIsEmpty = (inner) => {
  const text = String(inner || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim();
  return !text && !/data-token=/i.test(inner) && !/\{\{/.test(inner);
};

const placeSmsCustomNote = (html, note) => {
  const cleaned = String(note || '').trim();
  const noteHtml = cleaned
    ? `<div data-region="custom_note"><p>${escapePreviewText(cleaned)}</p></div>`
    : '';
  const block =
    /<div\b[^>]*data-region=["']custom_note["'][^>]*>([\s\S]*?)<\/div>/gi;
  let source = String(html || '');
  if (!block.test(source)) {
    return cleaned ? `${source}${noteHtml}` : source;
  }
  let placed = false;
  source = source.replace(
    /<div\b[^>]*data-region=["']custom_note["'][^>]*>([\s\S]*?)<\/div>/gi,
    (full, inner) => {
      if (!noteSlotIsEmpty(inner)) {
        return inner;
      }
      if (placed) {
        return '';
      }
      placed = true;
      return noteHtml;
    }
  );
  if (cleaned && !placed) {
    source = `${source}${noteHtml}`;
  }
  return source;
};

const mergePreviewHtml = (html, tokens = {}, customNote = '', channel = 'sms') => {
  let out = stripEditorChrome(html);
  out = out.replace(
    /<div[^>]*data-mcc-theme=["']?1["']?[^>]*>[\s\S]*?<\/div>/gi,
    ''
  );
    out = out.replace(/<span[^>]*mcc-card-tools[^>]*>[\s\S]*?<\/span>/gi, '');
    out = out.replace(
    /<button[^>]*mcc-card-delete[^>]*>[\s\S]*?<\/button>/gi,
    ''
  );
  if (channel === 'email') {
    out = rewriteTotalDueRow(out);
    out = out.replace(
      /<div[^>]*data-region=["']custom_note["'][^>]*>[\s\S]*?<\/div>/gi,
      ''
    );
    out = out.replace(/<div[^>]*data-card=["']note["'][^>]*>\s*<\/div>/gi, '');
    out = out.replace(/<span[^>]*data-logo-hint[^>]*>[\s\S]*?<\/span>/gi, '');
    out = out.replace(
      /(<[^>]*data-logo-slot=["']pharmacy["'][^>]*style=["'])([^"']*)(["'])/gi,
      (full, open, style, close) =>
        `${open}${style
          .replace(/border(?:-[a-z]+)?\s*:[^;"]*;?\s*/gi, '')
          .replace(/padding\s*:[^;"]*;?\s*/gi, '')}border:0;padding:0;${close}`
    );
    out = out.replace(/<span[^>]*data-lock-badge[^>]*>[\s\S]*?<\/span>/gi, '');
    out = out.replace(
      /<table\b[^>]*data-card=["']footer_note["'][^>]*>[\s\S]*?<\/table>/gi,
      ''
    );
    out = out.replace(
      /<div[^>]*data-region=["']footer_note["'][^>]*>[\s\S]*?<\/div>/gi,
      ''
    );
    if (!/data-logo=["']pharmacy["']/i.test(out)) {
      out = out.replace(
        /<td[^>]*data-logo-slot=["']pharmacy["'][^>]*>[\s\S]*?<\/td>\s*<td[^>]*width="1"[^>]*>[\s\S]*?<\/td>/i,
        ''
      );
      out = out.replace(
        /<span[^>]*data-logo-slot=["']pharmacy["'][^>]*>[\s\S]*?<\/span>/i,
        ''
      );
    }
  } else {
    out = placeSmsCustomNote(out, customNote);
  }
  out = out.replace(
    /<(span|p)([^>]*\sdata-token=["']([^"']+)["'][^>]*)>([\s\S]*?)<\/\1>/gi,
    (full, tag, _attrs, name) => {
      if (channel === 'email' && String(name || '').trim() === 'Link') {
        return '';
      }
      const value = tokenPreviewValue(name, tokens);
      if (value == null) {
        return full;
      }
      const raw = String(value);
      if (
        String(name || '').trim() === 'Account Logo' &&
        raw.indexOf('<') === -1 &&
        /^https?:\/\//i.test(raw)
      ) {
        return accountLogoImgHtml(raw);
      }
      if (raw.indexOf('<') !== -1) {
        return raw;
      }
      return `<${tag} class="mceNonEditable">${escapePreviewText(raw)}</${tag}>`;
    }
  );
  out = out.replace(
    /<img([^>]*\sdata-token=["']([^"']+)["'][^>]*)\/?>/gi,
    (full, attrs, name) => {
      const value = tokenPreviewValue(name, tokens);
      if (value == null) {
        return full;
      }
      const raw = String(value);
      if (String(name || '').trim() === 'Account Logo') {
        const existing = (String(attrs).match(/\ssrc=["']([^"']+)["']/i) || [])[1] || '';
        if (
          existing &&
          /^https?:\/\//i.test(existing) &&
          !/ilocal-logo-primary/i.test(existing)
        ) {
          return full;
        }
      }
      if (raw.indexOf('<') === 0) {
        return raw;
      }
      if (/^https?:\/\//i.test(raw)) {
        if (/\ssrc=/i.test(attrs)) {
          return `<img${attrs.replace(
            /\ssrc=["'][^"']*["']/i,
            ` src="${escapePreviewText(raw)}"`
          )}>`;
        }
        return `<img src="${escapePreviewText(raw)}"${attrs}>`;
      }
      return escapePreviewText(raw);
    }
  );
  const linkValue = tokenPreviewValue('Link', tokens);
  if (linkValue) {
    const href = escapePreviewText(String(linkValue));
    out = out.replace(
      /(href|data-href)=["']\{\{\s*Link\s*\}\}["']/gi,
      `$1="${href}"`
    );
  }
  if (channel === 'email') {
    out = out.replace(/\{\{\s*Link\s*\}\}/g, '');
    out = out.replace(
      /(Pay Now<\/a>)\s*(?:<span[^>]*>)?(?:https?:\/\/)?ilocal\.link\/[^<\s]+(?:<\/span>)?/gi,
      '$1'
    );
  }
  out = out.replace(TOKEN_RE, (full, raw) => {
    if (channel === 'email' && String(raw || '').trim() === 'Link') {
      return '';
    }
    const value = tokenPreviewValue(raw, tokens);
    if (value == null) {
      return full;
    }
    const next = String(value);
    if (
      String(raw || '').trim() === 'Account Logo' &&
      next.indexOf('<') === -1 &&
      /^https?:\/\//i.test(next)
    ) {
      return accountLogoImgHtml(next);
    }
    return next.indexOf('<') !== -1 ? next : escapePreviewText(next);
  });
  out = out.replace(
    /<p[^>]*>\s*(<table[\s\S]*?<\/table>)\s*<\/p>/gi,
    '$1'
  );
  return out;
};

const smsPreviewMarkup = (html) => {
  let out = String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/\ssrc\s*=\s*(['"])\s*javascript:[\s\S]*?\1/gi, '');
  out = out.replace(/>([^<]*)</g, (full, text) => {
    const linked = text.replace(LINK_RE, (url) => {
      const safe = escapePreviewText(url);
      return `<span class="mcc-sms-link">${safe}</span>`;
    });
    return `>${linked}<`;
  });
  return out.trim();
};

const htmlToPreviewText = (html) =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const renderSmsText = (text) => {
  const value = String(text || '');
  if (!value) {
    return 'The patient SMS preview will appear here.';
  }
  const parts = value.split(LINK_RE);
  return parts.map((part, index) => {
    if (!part) return null;
    if (/^(https?:\/\/|ilocal\.link\/)/i.test(part)) {
      return (
        <span className="mcc-sms-link" key={`link-${index}`}>
          {part}
        </span>
      );
    }
    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
};

const SmsPhonePreview = ({
  contact,
  initial,
  smsText,
  smsHtml,
  preview,
  previewBusy,
  t,
}) => {
  const chipClass = [
    'mcc-preview-chip',
    preview && preview.blocked ? 'is-blocked' : '',
    preview && preview.warning && !(preview && preview.blocked) ? 'is-warn' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mcc-preview-stage">
      <PreviewKicker
        label={t('MessageControl.SmsPreview', {
          defaultValue: 'Patient SMS preview',
        })}
        busy={previewBusy}
        t={t}
      />
      <div className="mcc-phone">
        <div className="mcc-phone-bezel">
          <div className="mcc-phone-notch" />
          <div className="mcc-phone-status">
            <span>9:41</span>
            <span className="mcc-phone-status-icons">LTE ⬤</span>
          </div>
          <div className="mcc-phone-header">
            <div className="mcc-phone-avatar">{initial}</div>
            <div>
              <div className="mcc-phone-contact">{contact}</div>
              <div className="mcc-phone-sub">
                {t('MessageControl.TextMessage', {
                  defaultValue: 'Text Message',
                })}
              </div>
            </div>
          </div>
          <div className="mcc-phone-thread">
            <div className="mcc-sms-time">Today 9:41 AM</div>
            {smsHtml ? (
              <div
                className="mcc-sms-bubble is-rich"
                dangerouslySetInnerHTML={{ __html: smsHtml }}
              />
            ) : (
              <div className="mcc-sms-bubble">{renderSmsText(smsText)}</div>
            )}
          </div>
          <div className="mcc-phone-home" />
        </div>
      </div>
      {preview ? (
        <div className={chipClass}>
          <strong>
            {preview.encoding || 'GSM'} · {preview.segmentCount || 0}{' '}
            {t('MessageControl.Segments', { defaultValue: 'segments' })}
          </strong>
          {preview.warning ? <span>{preview.warning}</span> : null}
          {preview.blocked ? <span>{preview.blockReason}</span> : null}
        </div>
      ) : null}
    </div>
  );
};

const EmailClientPreview = ({
  contact,
  initial,
  subject,
  html,
  previewBusy,
  t,
  onRemoveCard,
  removableCards,
}) => {
  const onRemoveRef = useRef(onRemoveCard);
  onRemoveRef.current = onRemoveCard;
  const removableRef = useRef(removableCards);
  removableRef.current = removableCards;
  const canRemove = typeof onRemoveCard === 'function';
  const bodyText = parseBrandColors(html).text;
  const frameHtml = `<!doctype html><html><head><meta charset="utf-8" /><style>
    html, body { margin: 0; padding: 0; background: #fff; }
    body { font-family: Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #0f172a; padding: 16px; }
    img { max-width: 100%; height: auto; }
    table { border-collapse: collapse; }
    [data-mcc-theme] { display: none !important; }
    [data-pickup-code-chip] { background-color: #F0F2F5 !important; border-radius: 8px !important; padding: 12px 8px !important; margin: 0 0 12px !important; }
    [data-pickup-code-chip] p, [data-pickup-code-chip] span { letter-spacing: 6px !important; font-size: 30px !important; font-weight: 700 !important; line-height: 34px !important; text-align: center !important; color: #1e3a5f !important; background: transparent !important; padding: 0 !important; }
    [data-mcc-total-due] { display: table !important; width: 100% !important; max-width: 100% !important; min-width: 0 !important; table-layout: auto !important; }
    [data-mcc-total-due] tbody { display: table-row-group !important; }
    [data-mcc-total-due] tr { display: table-row !important; }
    [data-mcc-total-due] td { display: table-cell !important; vertical-align: middle !important; }
    [data-mcc-total-due] td:first-child { font-size: 14px !important; font-weight: 400 !important; color: ${bodyText} !important; text-align: left !important; width: auto !important; }
    [data-mcc-total-due] td:last-child { font-size: 20px !important; font-weight: 700 !important; color: #1e3a5f !important; text-align: right !important; white-space: nowrap !important; width: auto !important; padding-left: 16px !important; }
    [data-mcc-pay-now-wrap] { text-align: center !important; width: 100% !important; }
    [data-mcc-pay-now-wrap] [data-mcc-pay-note] { text-align: center !important; }
    [data-mcc-total-due] .mceNonEditable { background: transparent !important; padding: 0 !important; font: inherit !important; color: inherit !important; }
    [data-mcc-link], span[data-token="Link"] { display: none !important; }
    a[data-brand="button"] { color: #ffffff !important; text-decoration: none !important; }
    [data-qr-mark] { position: relative !important; width: 148px !important; height: 148px !important; margin: 0 auto !important; }
    [data-qr-mark] img[data-qr-src] { width: 148px !important; height: 148px !important; max-width: 148px !important; }
    [data-qr-mark] img[data-brand="qr-mark"] { position: absolute !important; left: 50% !important; top: 50% !important; width: 30px !important; height: 40px !important; max-width: 30px !important; margin: -24px 0 0 -19px !important; background: #ffffff !important; border-radius: 6px !important; padding: 4px !important; box-sizing: content-box !important; }
    ${canRemove ? '' : '.mcc-card-delete { display: none !important; }'}
    [data-logo-hint], [data-lock-badge] { display: none !important; }
    [data-logo-slot="pharmacy"] { border: 0 !important; outline: none !important; padding: 0 !important; }
    [data-logo="pharmacy"] { display: inline-block; max-height: 36px; max-width: 110px; width: auto; height: auto; background: #fff; border-radius: 4px; padding: 3px; border: 0 !important; outline: none !important; }
    [data-card="footer-logo"], [data-card="branding"] img:not([data-logo]), [data-locked-widget="powered_by"] img { display: inline; width: 72px; max-width: 72px; height: auto; vertical-align: middle; margin: 0 0 0 4px; }
    div.mcc-layout-card { display: block; margin: 0 0 12px; position: relative; }
    td.mcc-layout-card, th.mcc-layout-card { display: table-cell !important; margin: 0 !important; position: relative; }
    td.stack { display: table-cell !important; }
    table { border-collapse: collapse; }
    table[data-mcc-canvas] { width: 100% !important; }
    table.wrap { width: 100% !important; min-width: 0 !important; max-width: 100% !important; table-layout: fixed !important; }
    table.wrap > tbody > tr > td:only-child, table.wrap > tr > td:only-child { width: auto !important; }
    table.mcc-layout-card:not([data-mcc-size]) { width: 100% !important; }
    table[data-card="hours"] > tbody > tr > td, table[data-card="hours"] > tr > td { vertical-align: top; }
    table[data-card="hours"] > tbody > tr > td:not([data-mcc-size]), table[data-card="hours"] > tr > td:not([data-mcc-size]) { width: 50% !important; }
    table[data-card="hours"] > tbody > tr > td:only-child, table[data-card="hours"] > tr > td:only-child { width: 100% !important; }
    img[data-logo="account"] { background: #ffffff; border-radius: 6px; padding: 6px 10px; max-height: 48px; max-width: 200px; height: auto; width: auto !important; display: block; margin: 0 auto; }
    .mcc-email-outro { display: block; width: 100% !important; margin: 0; }
    .mcc-email-outro, table.mcc-layout-card { margin: 0 0 12px; border-collapse: collapse; position: relative; }
    .mcc-email-outro, table.mcc-layout-card:not([data-mcc-size]) { width: 100% !important; }
    .mcc-email-outro, .mcc-email-outro table.mcc-layout-card, table.wrap > tbody > tr > td > table.mcc-layout-card, table.wrap > tr > td > table.mcc-layout-card { margin: 0 !important; }
    .mcc-layout-card { outline: 1px dashed transparent; }
    ${canRemove ? `
    .mcc-layout-card:hover, table.mcc-layout-card:hover { outline: 1px dashed #94a3b8; }
    .mcc-card-delete {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 26px;
      height: 26px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      background: #0f172a;
      color: #ffffff;
      font-size: 18px;
      line-height: 26px;
      cursor: pointer;
      z-index: 6;
      box-shadow: 0 1px 4px rgba(15, 23, 42, 0.35);
    }
    .mcc-card-delete:hover { background: #b91c1c; }
    ` : ''}
    .mcc-order-summary {
      width: 100% !important;
      border-collapse: collapse;
      margin: 4px 0 8px;
      border: 0;
      font-size: 13px;
    }
    .mcc-order-summary th {
      padding: 0 8px 10px 0;
      text-align: left;
      color: ${bodyText};
      font-size: 14px;
      font-weight: 700;
      border-bottom: 1px solid #94a3b8;
    }
    .mcc-order-summary td {
      padding: 12px 8px 12px 0;
      text-align: left;
      color: ${bodyText};
      font-size: 14px;
      vertical-align: top;
      border-bottom: 1px solid #cbd5e1;
    }
    .mcc-order-summary strong { color: ${bodyText}; font-weight: 400; }
    [data-mcc-link] { display: none !important; }
  </style></head><body>${html || ''}</body></html>`;
  const frameRef = useRef(null);
  const lastPaintedRef = useRef('');
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) {
      return undefined;
    }
    const decorate = (doc) => {
      if (!canRemove || !doc || !doc.body) {
        return;
      }
      const sourceCards = removableRef.current || [];
      const cards = collectRemovableLayoutCardNodes(doc.body);
      const seenById = {};
      cards.forEach((node) => {
        if (node.querySelector('.mcc-card-delete')) {
          return;
        }
        const id = node.getAttribute('data-card') || '';
        seenById[id] = (seenById[id] || 0) + 1;
        const occurrence = seenById[id];
        let seen = 0;
        let index = -1;
        for (let i = 0; i < sourceCards.length; i++) {
          if (sourceCards[i].id === id) {
            seen += 1;
            if (seen === occurrence) {
              index = sourceCards[i].index;
              break;
            }
          }
        }
        if (index < 0) {
          return;
        }
        const btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'mcc-card-delete';
        btn.setAttribute('title', 'Remove section');
        btn.setAttribute('data-remove-index', String(index));
        btn.appendChild(doc.createTextNode('×'));
        if (node.style) {
          node.style.position = 'relative';
        }
        if (
          node.getAttribute('data-card') === 'footer' ||
          node.nodeName === 'TABLE'
        ) {
          const row = node.querySelector('tr');
          const cells = row ? row.querySelectorAll('td, th') : [];
          const last = cells.length ? cells[cells.length - 1] : null;
          if (last) {
            if (last.style) {
              last.style.position = 'relative';
            }
            last.insertBefore(btn, last.firstChild);
            return;
          }
        }
        node.insertBefore(btn, node.firstChild);
      });
      doc.body.addEventListener('click', (event) => {
        let target = event.target;
        if (target && target.nodeType === 3) {
          target = target.parentNode;
        }
        const btn =
          target && target.closest ? target.closest('.mcc-card-delete') : null;
        if (!btn) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const index = parseInt(btn.getAttribute('data-remove-index'), 10);
        if (Number.isFinite(index) && onRemoveRef.current) {
          onRemoveRef.current(index);
        }
      });
    };
    const fitFrame = (frame) => {
      const doc = frame && frame.contentDocument;
      if (!doc || !doc.body) {
        return;
      }
      const height = Math.max(
        doc.body.scrollHeight || 0,
        doc.documentElement ? doc.documentElement.scrollHeight : 0,
        240
      );
      frame.style.height = `${height + 16}px`;
      Array.prototype.forEach.call(doc.images || [], (img) => {
        if (img.getAttribute('data-mcc-fit') === '1') {
          return;
        }
        img.setAttribute('data-mcc-fit', '1');
        img.addEventListener('load', () => fitFrame(frame));
      });
    };
    const paint = () => {
      try {
        const doc = frame.contentDocument;
        if (!doc) {
          return;
        }
        doc.open();
        doc.write(frameHtml);
        doc.close();
        decorate(doc);
        fitFrame(frame);
      } catch (_err) {
        frame.onload = () => {
          try {
            decorate(frame.contentDocument);
            fitFrame(frame);
          } catch (ignored) {
            /* preview stays read-only if the iframe cannot be decorated */
          }
        };
        frame.srcdoc = frameHtml;
      }
    };
    const paintKey = `${canRemove ? '1' : '0'}:${frameHtml}`;
    if (lastPaintedRef.current === paintKey) {
      return undefined;
    }
    const runPaint = () => {
      lastPaintedRef.current = paintKey;
      paint();
    };
    runPaint();
    return undefined;
  }, [frameHtml, canRemove]);
  return (
  <div className="mcc-preview-stage is-email">
    <PreviewKicker
      label={t('MessageControl.EmailPreview', {
        defaultValue: 'Patient email preview',
      })}
      busy={previewBusy}
      t={t}
    />
    {canRemove ? (
      <p className="mcc-preview-remove-hint">
        {t('MessageControl.PreviewRemoveHint', {
          defaultValue: 'Click × on a section in the preview to remove it.',
        })}
      </p>
    ) : null}
    {canRemove && removableCards && removableCards.length ? (
      <div className="mcc-preview-card-strip">
        {removableCards.map((card) => (
          <button
            type="button"
            className="mcc-preview-card-chip"
            key={`${card.id}-${card.index}`}
            title={t('MessageControl.RemoveCard', {
              defaultValue: 'Remove section',
            })}
            onClick={() => {
              if (onRemoveRef.current) {
                onRemoveRef.current(card.index);
              }
            }}
          >
            <span>{card.label}</span>
            <span aria-hidden="true">×</span>
          </button>
        ))}
      </div>
    ) : null}
    <div className="mcc-email-client">
      <div className="mcc-email-toolbar">
        <span className="mcc-email-toolbar-dot is-red" />
        <span className="mcc-email-toolbar-dot is-amber" />
        <span className="mcc-email-toolbar-dot is-green" />
        <span className="mcc-email-toolbar-label">
          {t('MessageControl.Inbox', { defaultValue: 'Inbox' })}
        </span>
      </div>
      <div className="mcc-email-meta">
        <div className="mcc-email-avatar">{initial}</div>
        <div className="mcc-email-meta-copy">
          <div className="mcc-email-from-row">
            <strong>{contact}</strong>
            <span className="mcc-email-time">9:41 AM</span>
          </div>
          <div className="mcc-email-address">
            {t('MessageControl.EmailFrom', { defaultValue: 'From' })}:{' '}
            pharmacy@{String(contact || 'pharmacy')
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '')
              .slice(0, 18) || 'pharmacy'}
            .com
          </div>
          <div className="mcc-email-address">
            {t('MessageControl.EmailTo', { defaultValue: 'To' })}: Maria Garcia
            &lt;maria.garcia@email.com&gt;
          </div>
        </div>
      </div>
      <h2 className="mcc-email-subject">{subject}</h2>
      <div className="mcc-email-letter">
          <iframe
          ref={frameRef}
          className="mcc-email-preview-frame"
          title={t('MessageControl.EmailPreview', {
            defaultValue: 'Patient email preview',
          })}
          scrolling="no"
          sandbox="allow-same-origin"
        />
      </div>
    </div>
  </div>
  );
};

const hexToRgba = (hex, alpha) => {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return undefined;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return undefined;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const MobileBrowserPreview = ({
  contact,
  firstName,
  headline,
  intro,
  ctaLabel,
  greeting,
  arrivalLabel,
  customNote,
  customMessage,
  locationName,
  methodLabel,
  buttonColor,
  accentColor,
  hideAmountDue,
  paymentEnabled = true,
  logoUrl,
  t,
}) => {
  const payColor = buttonColor || '#2563eb';
  const accent = accentColor || '#2563eb';
  const greetingLine =
    greeting ||
    t('MessageControl.LinkGreetingDefault', {
      defaultValue: 'we have you ready for Curbside pick up.',
    });
  const pageMessage = customMessage || customNote;
  return (
    <div className="mcc-preview-stage">
      <div className="mcc-preview-kicker">
        {t('MessageControl.MobilePreview', {
          defaultValue: 'Patient link page preview',
        })}
      </div>
      <div className="mcc-phone is-browser">
        <div className="mcc-phone-bezel">
          <div className="mcc-phone-notch" />
          <div className="mcc-phone-status is-light">
            <span>9:41</span>
            <span className="mcc-phone-status-icons">LTE ⬤</span>
          </div>
          <div className="mcc-browser-bar">
            <span className="mcc-browser-lock" role="img" aria-label="Secure">
              🔒
            </span>
            <span className="mcc-browser-url">ilocal.link/xxxx</span>
          </div>
          <div className="mcc-browser-page is-link-page">
            <div className="mcc-link-logo">
              {logoUrl ? (
                <img src={logoUrl} alt="" />
              ) : (
                contact
              )}
            </div>
            <p className="mcc-link-greeting">
              Hi <strong>{firstName || 'Maria'}</strong>, {greetingLine}
            </p>
            <div className="mcc-link-address">
              <div>123 MAIN STREET</div>
              <div>Santee, CA 98745</div>
              <span
                className="mcc-link-badge"
                style={{ backgroundColor: accent }}
              >
                {methodLabel ||
                  t('MessageControl.Counter', { defaultValue: 'Counter' })}
              </span>
            </div>
            <div
              className={
                pageMessage
                  ? 'mcc-link-custom-message'
                  : 'mcc-link-custom-message is-placeholder'
              }
            >
              {pageMessage ||
                t('MessageControl.LinkCustomMessagePlaceholder', {
                  defaultValue: 'Custom message from Studio',
                })}
            </div>
            <div
              className="mcc-link-steps"
              style={{
                backgroundColor: hexToRgba(accent, 0.08),
                borderColor: hexToRgba(accent, 0.35),
              }}
            >
              <strong style={{ color: accent }}>
                {headline ||
                  t('MessageControl.StepsTitle', {
                    defaultValue: 'Complete Required Steps',
                  })}
              </strong>
              <p style={{ color: accent }}>
                {intro ||
                  t('MessageControl.StepsHelper', {
                    defaultValue:
                      'Please complete all required steps below (payment, signature, ID capture, consultation) and click the "Checkout" button when you\'re ready.',
                  })}
              </p>
            </div>
            <div className="mcc-link-summary-title">Order Summary</div>
            <div className="mcc-link-rx">
              <div>
                <strong>Eliquis 2.5 mg Tab</strong>
                <span>Rx: A11945</span>
              </div>
              {paymentEnabled ? <em>$5.00</em> : null}
            </div>
            {paymentEnabled ? (
              <div className="mcc-link-totals">
                <div>
                  <span>Order Total:</span>
                  <strong>$5.00</strong>
                </div>
                <div>
                  <span>Amount Paid:</span>
                  <strong>$0.00</strong>
                </div>
                {hideAmountDue ? null : (
                  <div className="is-due">
                    <span>Balance Due:</span>
                    <strong>$5.00</strong>
                  </div>
                )}
              </div>
            ) : null}
            {paymentEnabled ? (
              <button
                type="button"
                className="mcc-link-pay"
                style={{ backgroundColor: payColor }}
              >
                {ctaLabel ||
                  t('MessageControl.PayNow', { defaultValue: 'Pay Now' })}
                <span>Required</span>
              </button>
            ) : null}
            <div className="mcc-link-secondary">
              <button type="button">Video Consult</button>
              <button type="button" className="is-id">
                Capture ID
              </button>
            </div>
            <div className="mcc-link-sign">Please sign here</div>
            <button type="button" className="mcc-link-checkout">
              Checkout
            </button>
            <div className="mcc-link-hours">
              <strong>Store Hours</strong>
              <div>
                <span>Monday</span>
                <span>9:00 AM - 8:00 PM</span>
              </div>
              <div>
                <span>
                  {methodLabel || locationName || 'Location'}
                </span>
                <span>Open 24 Hours</span>
              </div>
            </div>
            <div className="mcc-browser-powered">
              Powered by ilocalbox.com
            </div>
            {arrivalLabel ? (
              <div className="mcc-link-arrival-hint">
                After checkout: {arrivalLabel}
              </div>
            ) : null}
          </div>
          <div className="mcc-phone-home" />
        </div>
      </div>
    </div>
  );
};

const MessagePreview = ({
  channel,
  preview,
  previewBusy,
  html,
  subject,
  accountName,
  tokens,
  headline,
  intro,
  ctaLabel,
  greeting,
  arrivalLabel,
  customNote,
  customMessage,
  locationName,
  methodLabel,
  buttonColor,
  accentColor,
  hideAmountDue,
  paymentEnabled = true,
  logoUrl,
  onRemoveCard,
  t,
}) => {
  const contact =
    accountName ||
    t('MessageControl.Pharmacy', { defaultValue: 'Pharmacy' });
  const initial = String(contact).trim().charAt(0).toUpperCase() || 'P';
  const tokenBag = tokens || {
    'Account Name': contact,
    'First Name': 'Maria',
  };
  const mergedSubject = mergePreviewText(
    subject || (preview && preview.subject) || '',
    tokenBag
  );
  const liveHtml = mergePreviewHtml(
    channel === 'email' ? html || '' : html || '',
    tokenBag,
    customNote,
    channel
  );
  const emailHtml = liveHtml;
  const smsMarkup = smsPreviewMarkup(liveHtml);
  const smsText =
    htmlToPreviewText(liveHtml) || (preview && preview.plainText) || '';
  const smsHtml = /<img\b/i.test(smsMarkup) ? smsMarkup : '';

  if (channel === 'mobile') {
    return (
      <MobileBrowserPreview
        contact={contact}
        firstName={tokenBag['First Name'] || 'Maria'}
        headline={headline}
        intro={intro}
        ctaLabel={ctaLabel}
        greeting={greeting}
        arrivalLabel={arrivalLabel}
        customNote={customNote}
        customMessage={customMessage}
        locationName={locationName}
        methodLabel={methodLabel}
        buttonColor={buttonColor}
        accentColor={accentColor}
        hideAmountDue={paymentEnabled ? hideAmountDue : true}
        paymentEnabled={paymentEnabled}
        logoUrl={logoUrl}
        t={t}
      />
    );
  }

  if (channel === 'email') {
    return (
      <EmailClientPreview
        contact={contact}
        initial={initial}
        subject={
          mergedSubject ||
          t('MessageControl.Subject', { defaultValue: 'Subject' })
        }
        html={emailHtml}
        previewBusy={previewBusy}
        t={t}
        onRemoveCard={onRemoveCard}
        removableCards={
          typeof onRemoveCard === 'function'
            ? listRemovableLayoutCards(html)
            : []
        }
      />
    );
  }

  return (
    <SmsPhonePreview
      contact={contact}
      initial={initial}
      smsText={smsText}
      smsHtml={smsHtml}
      preview={preview}
      previewBusy={previewBusy}
      t={t}
    />
  );
};

export default MessagePreview;



// WEBPACK FOOTER //
// ./src/components/message-control-center/MessagePreview.js